import { createSelector } from "@reduxjs/toolkit";

import { selectThemeItem } from "../app/appSlice";
import { selectConfigItem } from "../config/configSlice";
import { selectHasUsableLocation } from "../location/locationSlice";
import { selectMapViewLimits, selectMapViewState } from "../map/mapSlice";

import type { RootState } from "@/app/store";
import { MAX_ZOOM } from "@/features/map/constants";
import { getPoiIdFromIndicationMetaData } from "@/features/utils/navigation";
import {
  computeCurrentSegments,
  computeFloorChanges,
  computeRouteLine,
  mergePaths,
  obtainEdgePositions,
  transformRouteSegments,
} from "@/features/utils/routes";
import {
  selectCurrentBuilding,
  selectCurrentFloorID,
} from "../cartography/cartographySlice.selectors";

// ----------------
// General selectors

const sliceData = (state: RootState) => state.navigation;

export const selectNavigationTo = createSelector(sliceData, (item) => {
  return item.navigationTo;
});

export const selectNavigationFrom = createSelector(sliceData, (item) => {
  return item.navigationFrom;
});

export const selectNavigationRoute = createSelector(sliceData, (item) => {
  return item.route;
});

export const selectNavigationRouteId = createSelector(sliceData, (item) => {
  return item.route?.identifier;
});

export const selectNavigationRouteType = createSelector(sliceData, (item) => {
  return item.tags[0];
});

export const selectNavigationNearest = createSelector(sliceData, (item) => {
  return {
    destination: item.nearestDestinationNode,
    origin: item.nearestOriginNode,
  };
});

export const selectNavigationCurrentStep = createSelector(sliceData, (item) => {
  return item.route?.currentStep;
});

export const selectNavigationCurrentIndication = createSelector(
  sliceData,
  (item) => {
    return item.route?.indications?.[0] ?? null;
  },
);

export const selectNavigationErrors = createSelector(sliceData, (item) => {
  return item.errors;
});

export const selectNavigationStatus = createSelector(sliceData, (item) => {
  return item.navigationStatus;
});

export const selectPendingNavigationRequest = createSelector(
  sliceData,
  (item) => {
    return item.pendingNavigationRequest;
  },
);

export const selectNavigationWaitingForLocation = createSelector(
  [selectPendingNavigationRequest, selectHasUsableLocation],
  (pendingNavigationRequest, hasUsableLocation) => {
    return Boolean(pendingNavigationRequest && !hasUsableLocation);
  },
);

export const selectNavigationName = createSelector(sliceData, (item) => {
  return item.navigationName;
});

export const selectLastNavigationTo = createSelector(sliceData, (item) => {
  return item.lastNavigationTo;
});

export const selectNavigationRouteTags = createSelector(sliceData, (item) => {
  return item.tags;
});

export const selectSpeakAloudIndications = createSelector(sliceData, (item) => {
  return item.speakAloudIndications;
});

export const selectCurrentNavigationRoutePois = createSelector(
  sliceData,
  (item) => {
    return item.route?.indications
      .reduce(
        (acc, x) => {
          if (x.metaData) {
            acc = [...acc, getPoiIdFromIndicationMetaData(x.metaData)];
          }
          return acc;
        },
        [] as (number | null | undefined)[],
      )
      .filter(Boolean);
  },
);

export const selectDestinationPosition = createSelector(sliceData, (item) => {
  return item.destinationPosition;
});

// ----------------
// Route processing

// Obtain list of segments on current floor
export const selectNavigationSegments = createSelector(
  [selectNavigationRoute, selectCurrentBuilding, selectCurrentFloorID],
  (route, currentBuilding, currentFloorID) => {
    return computeCurrentSegments(
      currentBuilding,
      route,
      currentFloorID,
      false,
      false,
    );
  },
);

export const selectVisibleNavigationSegments = createSelector(
  [selectNavigationRoute, selectCurrentBuilding, selectCurrentFloorID],
  (route, currentBuilding, currentFloorID) => {
    return computeCurrentSegments(
      currentBuilding,
      route,
      currentFloorID,
      false,
      true,
    );
  },
);

export const selectNavigationPath = createSelector(
  [
    selectCurrentBuilding,
    selectVisibleNavigationSegments,
    selectMapViewState,
    selectMapViewLimits,
    selectConfigItem("feature_flags.flat_earth"),
    selectThemeItem("route.pathDotGap"),
  ],
  (
    currentBuilding,
    segments,
    mapViewState,
    mapViewLimits,
    flatEarth,
    pathDotGap,
  ) => {
    if (!currentBuilding) return;

    const transformedRouteSegments = transformRouteSegments(
      segments,
      currentBuilding.geometry.corners.sw,
      currentBuilding.geometry.corners.se,
      currentBuilding.geometry.corners.ne,
      currentBuilding.geometry.width,
      currentBuilding.geometry.length,
      flatEarth,
    );

    const navigationLine = computeRouteLine({
      customOffset: pathDotGap,
      isEmbed: true,
      segments: transformedRouteSegments,
      // TODO: review maxZoom is sometimes not being computed correctly
      zoom: Math.min(mapViewState.zoom, mapViewLimits.maxZoom || MAX_ZOOM),
    });

    return mergePaths(navigationLine);
  },
);

// Obtain a list of floor changes, and the origin-destination mapping
export const selectNavigationFloorChanges = createSelector(
  [selectCurrentBuilding, selectNavigationRoute, selectNavigationSegments],
  (currentBuilding, route, segments) => {
    return computeFloorChanges({
      building: currentBuilding,
      isSelectedRoute: true,
      routeId: route?.identifier,
      segments: segments,
      steps: route?.steps,
    });
  },
);

export const selectNavigationEdgePoints = createSelector(
  [selectNavigationSegments],
  (segments) => {
    return obtainEdgePositions(segments);
  },
);
